import React from 'react';
import styled from 'styled-components';
import type { UploadFileEntry } from '../hooks/useSmartSortingUploads';
import { formatFileSize } from '../lib/file';

const QueueContainer = styled.div`
  margin-top: 1rem;
`;

const QueueHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const QueueTitle = styled.h4`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;

  &:hover {
    color: var(--text-secondary);
  }
`;

const FileList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FileItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  font-size: 0.875rem;
`;

const FileName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-primary);
`;

const FileSize = styled.span`
  color: var(--text-muted);
  font-size: 0.8rem;
  flex-shrink: 0;
`;

const StatusBadge = styled.span<{ $status: string }>`
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.15rem 0.5rem;
  border-radius: var(--radius-sm);
  flex-shrink: 0;

  ${(props) => {
    switch (props.$status) {
      case 'uploading':
      case 'finalizing':
        return `
          background: var(--brand-50, #eff6ff);
          color: var(--brand-700, #1d4ed8);
        `;
      case 'done':
        return `
          background: var(--success-50, #f0fdf4);
          color: var(--success-600, #15803d);
        `;
      case 'error':
        return `
          background: var(--danger-50, #fef2f2);
          color: var(--danger-600, #b91c1c);
        `;
      default:
        return `
          background: var(--surface-strong, #f3f4f6);
          color: var(--text-muted);
        `;
    }
  }}
`;

const ProgressBar = styled.div<{ $progress: number }>`
  height: 3px;
  background: var(--border);
  border-radius: 2px;
  flex: 1;
  min-width: 60px;
  max-width: 100px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${(props) => props.$progress}%;
    background: var(--brand-500);
    transition: width 0.3s ease;
  }
`;

const ErrorText = styled.span`
  font-size: 0.75rem;
  color: var(--danger-600);
`;

function statusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'En espera';
    case 'uploading':
      return 'Subiendo';
    case 'finalizing':
      return 'Procesando';
    case 'done':
      return 'Listo';
    case 'error':
      return 'Error';
    default:
      return status;
  }
}

interface SmartSortingUploadQueueProps {
  queue: UploadFileEntry[];
  onClearCompleted: () => void;
}

export const SmartSortingUploadQueue: React.FC<
  SmartSortingUploadQueueProps
> = ({ queue, onClearCompleted }) => {
  if (queue.length === 0) return null;

  const hasCompleted = queue.some(
    (e) => e.status === 'done' || e.status === 'error',
  );

  return (
    <QueueContainer>
      <QueueHeader>
        <QueueTitle>Archivos ({queue.length})</QueueTitle>
        {hasCompleted && (
          <ClearButton type="button" onClick={onClearCompleted}>
            Limpiar completados
          </ClearButton>
        )}
      </QueueHeader>
      <FileList>
        {queue.map((entry) => (
          <FileItem key={entry.id}>
            <FileName title={entry.file.name}>{entry.file.name}</FileName>
            <FileSize>{formatFileSize(entry.file.size)}</FileSize>
            {(entry.status === 'uploading' ||
              entry.status === 'finalizing') && (
              <ProgressBar $progress={entry.progress} />
            )}
            <StatusBadge $status={entry.status}>
              {statusLabel(entry.status)}
            </StatusBadge>
            {entry.error && <ErrorText>{entry.error}</ErrorText>}
          </FileItem>
        ))}
      </FileList>
    </QueueContainer>
  );
};
